import { ApiClient } from '../client/ApiClient';
import { ApiResponse } from '../types';
import { CountryCode, getMultiTenancyHeaders } from '../utils/multiTenancyHeaders';
import { GetKlaviyoCustomerApiResponse, UnsubscribeResponse, SubscriptionType } from './types';

export type OnlineMarketingApiClientConstructorArgs = {
  onlineMarketingApiBaseUrl: string;
  onlineMarketingApiKey?: string;
  countryCode: CountryCode;
};

export class OnlineMarketingApiClient extends ApiClient {
  private readonly _onlineMarketingApiBaseUrl: string;
  private readonly _onlineMarketingApiKey?: string;
  private readonly _countryCode: CountryCode;

  constructor({
    onlineMarketingApiBaseUrl,
    onlineMarketingApiKey,
    countryCode,
  }: OnlineMarketingApiClientConstructorArgs) {
    super();
    this._onlineMarketingApiBaseUrl = onlineMarketingApiBaseUrl;
    this._onlineMarketingApiKey = onlineMarketingApiKey;
    this._countryCode = countryCode;
  }

  override get requestHeaders(): Record<string, string> {
    return {
      ...super.requestHeaders,
      ...getMultiTenancyHeaders(this._countryCode),
      ...(this._onlineMarketingApiKey ? { 'x-jbg-api-key': this._onlineMarketingApiKey } : {}),
    };
  }
  async getKlaviyoCustomer({
    klaviyoId,
    campaign,
    fullUrl,
  }: {
    klaviyoId: string;
    campaign?: string | null;
    fullUrl?: string | null;
  }): Promise<ApiResponse<GetKlaviyoCustomerApiResponse>> {
    const body: Record<string, string> = { id: klaviyoId ?? '' };
    if (campaign) {
      body.campaign = campaign;
    }
    if (fullUrl) {
      body.fullUrl = fullUrl;
    }

    const { data, statusCode, error } = await this._fetch<GetKlaviyoCustomerApiResponse>({
      path: `${this._onlineMarketingApiBaseUrl}/GetKlaviyoCustomer?id=${klaviyoId}`,
      method: 'POST',
      body,
    });

    // throw errors to allow react query to handle retries for 5xx errors instead of empty data
    if (statusCode && statusCode >= 500) {
      throw new Error(error?.message || `OnlineMarketingApi error ${statusCode}`);
    }

    if (error) {
      return { error, statusCode };
    }

    return { data, error, statusCode };
  }

  async getKlaviyoCustomerByEmail({
    email,
    kxId,
    fullUrl,
    utmCampaign,
  }: {
    email: string;
    kxId?: string | null;
    fullUrl?: string | null;
    utmCampaign?: string | null;
  }): Promise<ApiResponse<GetKlaviyoCustomerApiResponse>> {
    const { data, statusCode, error } = await this._fetch<GetKlaviyoCustomerApiResponse>({
      path: `${this._onlineMarketingApiBaseUrl}/GetKlaviyoCustomerByEmail`,
      method: 'POST',
      body: {
        email,
        KxId: kxId ?? '',
        Url: fullUrl ?? '',
        UtmCampaign: utmCampaign ?? '',
      },
    });

    if (error) {
      return { error, statusCode };
    }
    return { data, error, statusCode };
  }

  async unsubscribe({
    klaviyoId,
    subscriptions,
    email,
  }: {
    klaviyoId: string | null;
    email?: string | null;
    subscriptions: Array<SubscriptionType>;
  }): Promise<ApiResponse<UnsubscribeResponse>> {
    const { data, statusCode, error } = await this._fetch<UnsubscribeResponse>({
      path: `${this._onlineMarketingApiBaseUrl}/Unsubscribe`,
      method: 'POST',
      body: JSON.stringify({
        klaviyoId,
        email,
        subscriptions,
      }),
    });

    return { data, statusCode, error };
  }
}
